import { FC, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loader } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import NuveiFormFields from "./nuvei-form-fields";
import NaxellaFormFields from "./naxella-form-fields";
import { useStorePaymentMethodService } from "../services/use-store-payment-method-service";
import useAddCardSchema, {
  AddCardFormValues,
} from "../hooks/use-add-card-schema";
import type { BillingInfo, RawGatewayConfig } from "../types";

interface Props {
  contractId: string | number;
  gatewayConfig: RawGatewayConfig;
  billingInfo: BillingInfo;
  paying?: boolean;
  submitLabel?: string;
  onSuccess: (methodId: string | number) => void;
  onError: (message: string) => void;
}

const AddCardForm: FC<Props> = ({
  contractId,
  gatewayConfig,
  billingInfo,
  paying = false,
  submitLabel,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation(["checkout"]);
  const schema = useAddCardSchema();
  const isNaxella = gatewayConfig.provider === "naxella";

  const [tokenizing, setTokenizingState] = useState(false);
  const tokenizingRef = useRef(false);
  const setTokenizing = (value: boolean) => {
    tokenizingRef.current = value;
    setTokenizingState(value);
  };
  const safeChargeRef = useRef<{
    safeCharge: any;
    scFields: { cardNumber: any; cardExpiry: any; cardCvc: any };
  } | null>(null);
  const naxellaRef = useRef<{ getToken: () => Promise<string> } | null>(null);

  const { mutate: storeMethod, isPending: storing } =
    useStorePaymentMethodService(contractId);

  const form = useForm<AddCardFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: billingInfo.first_name,
      last_name: billingInfo.last_name,
      email: billingInfo.email,
      cardholder_name: "",
      card_number: false,
      card_expiry: false,
      card_cvc: false,
    },
  });

  const fieldsDisabled = storing || paying;

  const onSubmit = async ({ cardholder_name, first_name, last_name, email }: AddCardFormValues) => {
    if (tokenizingRef.current || storing) return;
    const resolvedBillingInfo = { ...billingInfo, first_name, last_name, email };
    if (isNaxella) {
      if (!naxellaRef.current) return;

      setTokenizing(true);
      let ccTempToken: string;
      try {
        ccTempToken = await naxellaRef.current.getToken();
      } catch (e: any) {
        onError(
          e?.message ||
            t(
              "checkout:add-card-tokenization-failed",
              "Card tokenization failed. Please check your card details.",
            ),
        );
        setTokenizing(false);
        return;
      }
      // Keep the button disabled until the store request settles so it can't
      // be re-submitted in the gap before the mutation's isPending kicks in.
      storeMethod(
        {
          ...resolvedBillingInfo,
          cc_temp_token: ccTempToken,
          cardholder_name,
        },
        {
          onSettled: () => setTokenizing(false),
          onSuccess: (method) => onSuccess(method.id),
          onError: (err: any) =>
            onError(
              err?.response?.data?.message ??
                t(
                  "checkout:add-card-save-failed",
                  "Failed to save card. Please try again.",
                ),
            ),
        },
      );
      return;
    }

    // Nuvei flow
    if (!safeChargeRef.current) return;
    const { safeCharge, scFields } = safeChargeRef.current;

    // Disable the submit button via state, but NOT the card fields: unmounting
    // or disabling NuveiFields during getToken() detaches the iframes and
    // hangs the Promise (fieldsDisabled intentionally excludes `tokenizing`).
    setTokenizing(true);
    let ccTempToken: string;
    try {
      const result = await safeCharge.getToken(scFields.cardNumber, {
        sessionToken: (gatewayConfig as any).session_token,
        cardHolderName: cardholder_name,
      });

      if (result.status !== "SUCCESS" || !result.ccTempToken) {
        onError(
          result.reason ||
            result.error?.message ||
            t(
              "checkout:add-card-tokenization-failed",
              "Card tokenization failed. Please check your card details.",
            ),
        );
        setTokenizing(false);
        return;
      }

      ccTempToken = result.ccTempToken;
    } catch (e) {
      console.log("tokenization error", e);
      onError(
        t(
          "checkout:add-card-tokenization-failed",
          "Card tokenization failed. Please check your card details.",
        ),
      );
      setTokenizing(false);
      return;
    }
    // Keep the button disabled until the store request settles so it can't
    // be re-submitted in the gap before the mutation's isPending kicks in.
    storeMethod(
      {
        ...resolvedBillingInfo,
        cc_temp_token: ccTempToken,
        cardholder_name,
        session_token: (gatewayConfig as any).session_token,
      },
      {
        onSettled: () => setTokenizing(false),
        onSuccess: (method) => onSuccess(method.id),
        onError: (err: any) =>
          onError(
            err?.response?.data?.message ??
              t(
                "checkout:add-card-save-failed",
                "Failed to save card. Please try again.",
              ),
          ),
      },
    );
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("checkout:add-card-first-name", "First name")}
                  <span className="text-red-500 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={fieldsDisabled}
                    placeholder={t("checkout:add-card-first-name", "First name")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("checkout:add-card-last-name", "Last name")}
                  <span className="text-red-500 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={fieldsDisabled}
                    placeholder={t("checkout:add-card-last-name", "Last name")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("checkout:add-card-email", "Email")}
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={fieldsDisabled}
                  placeholder={t("checkout:add-card-email", "Email")}
                  type="email"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cardholder_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("checkout:add-card-cardholder-name", "Card holder name")}
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={fieldsDisabled}
                  maxLength={80}
                  placeholder={t(
                    "checkout:add-card-cardholder-name",
                    "Card holder name",
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isNaxella ? (
          <NaxellaFormFields
            control={form.control}
            disabled={fieldsDisabled}
            publicTokenizationKey={
              (
                gatewayConfig as {
                  provider: "naxella";
                  tokenization_key: string;
                }
              ).tokenization_key
            }
            onInit={(ctx) => {
              naxellaRef.current = ctx;
            }}
          />
        ) : (
          <NuveiFormFields
            control={form.control}
            disabled={fieldsDisabled}
            environment={(gatewayConfig as any).environment}
            merchantId={(gatewayConfig as any).merchant_id}
            merchantSiteId={(gatewayConfig as any).merchant_site_id}
            onInit={(ctx) => {
              safeChargeRef.current = ctx;
            }}
          />
        )}

        <Button
          type="submit"
          size="lg"
          variant="default"
          disabled={tokenizing || storing || paying}
          className="w-full"
        >
          {(tokenizing || storing || paying) && (
            <Loader className="size-4 animate-spin" />
          )}
          {submitLabel ?? t("checkout:add-card-save-and-pay", "Save card and pay")}
        </Button>
      </form>
    </FormProvider>
  );
};

export default AddCardForm;
